import { useForm } from "@tanstack/react-form";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { z } from "zod";
import eyeClose from "assets/img/eye-close.png";
import eyeOpen from "assets/img/eye-open.png";
import { Button, Input, Label, NoteMessage, ValidationMessage } from "components/elements";
import { cn } from "lib/cn";
import { trpc } from "lib/trpc";
import { TwoFactorAuthentication } from "screens/settings/TwoFactorAuthentication";
import { UserPasswordUpdateSchema } from "@compass/shared/types";

const UpdateUserPasswordFormSchema = UserPasswordUpdateSchema.extend({
    confirmNewPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmNewPassword, {
    message: "Passwords don't match",
    path: ["confirmNewPassword"],
});

type UpdateUserPasswordFormData = z.infer<typeof UpdateUserPasswordFormSchema>;

export const UpdateUserPasswordForm = ({ className }: { className?: string }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const updateUserPassword = trpc.users.updatePassword.useMutation({
        onSuccess: () => {
            enqueueSnackbar("Password has been updated successfully.", { variant: "success" });
        },
        onError: (e) => {
            console.error(e);
            enqueueSnackbar(e.message, { variant: "error" });
        },
    });

    const form = useForm({
        defaultValues: {
            currentPassword: "",
            newPassword: "",
            confirmNewPassword: "",
        },
        validators: {
            onSubmit: UpdateUserPasswordFormSchema,
        },
        onSubmit: async ({ value }) => {
            await updateUserPassword.mutateAsync({
                currentPassword: value.currentPassword,
                newPassword: value.newPassword,
            });
        },
    });

    return (
        <>
            <div className="mt-6 mb-4 font-sans text-[28px] font-semibold text-[#424242]">
                Security
            </div>
            <div className="bg-brand-polar rounded-2xl border border-[#c8c8c8] p-6">
                <div className="text-xl font-semibold text-[#424242]">Update Password</div>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        form.handleSubmit();
                    }}
                    className={cn("flex flex-col", className)}
                >
                    <Label htmlFor="current">Current Password</Label>
                    <form.Field name="currentPassword">
                        {(field) => (
                            <>
                                <div className="relative flex max-w-md items-center">
                                    <Input
                                        type={showCurrent ? "text" : "password"}
                                        placeholder="Current Password"
                                        autoComplete="current-password"
                                        className="flex-1 pr-10"
                                        name={field.name}
                                        value={field.state.value}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        onBlur={field.handleBlur}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowCurrent((prev) => !prev)}
                                        className="absolute right-2 flex items-center"
                                    >
                                        <img
                                            src={showCurrent ? eyeOpen : eyeClose}
                                            alt={showCurrent ? "Hide" : "Show"}
                                            className="h-4"
                                        />
                                    </button>
                                </div>
                                <ValidationMessage error={field.state.meta.errors[0]?.message} />
                            </>
                        )}
                    </form.Field>

                    <Label htmlFor="newPassword">New Password</Label>
                    <form.Field name="newPassword">
                        {(field) => (
                            <>
                                <div className="relative flex max-w-md items-center">
                                    <Input
                                        type={showNew ? "text" : "password"}
                                        placeholder="New Password"
                                        autoComplete="new-password"
                                        className="flex-1 pr-10"
                                        name={field.name}
                                        value={field.state.value}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        onBlur={field.handleBlur}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNew((prev) => !prev)}
                                        className="absolute right-2 flex items-center"
                                    >
                                        <img
                                            src={showNew ? eyeOpen : eyeClose}
                                            alt={showNew ? "Hide" : "Show"}
                                            className="h-4"
                                        />
                                    </button>
                                </div>
                                <NoteMessage
                                    note="Your password must be at least 6 characters long, containing at least one capital letter and one special character.
        We recommend using a password manager to generate secure passwords."
                                />
                                <ValidationMessage error={field.state.meta.errors[0]?.message} />
                            </>
                        )}
                    </form.Field>

                    <Label htmlFor="confirmNewPassword">Confirm New Password</Label>
                    <form.Field name="confirmNewPassword">
                        {(field) => (
                            <>
                                <div className="relative flex max-w-md items-center">
                                    <Input
                                        type={showConfirm ? "text" : "password"}
                                        placeholder="Confirm New Password"
                                        autoComplete="new-password"
                                        className="flex-1 pr-10"
                                        name={field.name}
                                        value={field.state.value}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        onBlur={field.handleBlur}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirm((prev) => !prev)}
                                        className="absolute right-2 flex items-center"
                                    >
                                        <img
                                            src={showConfirm ? eyeOpen : eyeClose}
                                            alt={showConfirm ? "Hide" : "Show"}
                                            className="h-4"
                                        />
                                    </button>
                                </div>
                                <ValidationMessage error={field.state.meta.errors[0]?.message} />
                            </>
                        )}
                    </form.Field>

                    <div className="h-4" />
                    <Button
                        type="submit"
                        variant="primary"
                        size="smaller"
                        className="mr-auto"
                        isLoading={updateUserPassword.isPending}
                        disabled={updateUserPassword.isPending}
                    >
                        Update Password
                    </Button>
                </form>
            </div>

            <TwoFactorAuthentication />
        </>
    );
};
